import { ThemeMenuItems } from '@/components/ThemeMenuItems';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePrincipal } from '@/hooks/usePrincipal';
import { signIn, signOut } from '@/lib/auth-actions';
import { cn } from '@/lib/utils';
import { EllipsisVertical, LogIn, LogOut, User, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

type UserMenuVariant = 'compact' | 'expanded';

interface UserMenuProps {
  /**
   * Layout mode.
   *
   *  - `'compact'` (default) — icon-only avatar button. Used by the top
   *    bar where horizontal space is at a premium.
   *  - `'expanded'` — full-width row with avatar + name/email + kebab.
   *    Used by the sidebar footer (matches the shadcn dashboard pattern).
   */
  variant?: UserMenuVariant;
  /**
   * Optional className forwarded to the trigger button; lets callers
   * tweak spacing without re-implementing the variant.
   */
  className?: string;
}

function AvatarGlyph() {
  // `!h-5 !w-5` beats the Button's `[&_svg]:size-4` descendant rule
  // (button.tsx:7), which otherwise pins every nested SVG to 16px.
  return (
    <Avatar className="h-8 w-8">
      <AvatarFallback className="bg-muted">
        <UserRound
          className="!h-5 !w-5 text-muted-foreground"
          strokeWidth={2.25}
          aria-hidden="true"
        />
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * Auth-aware avatar dropdown. Switches on the principal's discriminated
 * `status`:
 *
 *  - `'loading'` → disabled trigger with an avatar-shaped skeleton.
 *  - `'authenticated'` → avatar (+ name/email in `expanded`) + Profile /
 *    Sign out dropdown.
 *  - `'anonymous'` / `'error'` → "Sign in" button. (The error branch is
 *    treated as anonymous from a UX standpoint — a stuck `/api/auth/me`
 *    shouldn't hide the sign-in affordance.)
 */
export function UserMenu({ variant = 'compact', className }: UserMenuProps) {
  const principal = usePrincipal();

  if (principal.status === 'loading') {
    if (variant === 'expanded') {
      return (
        <Button
          variant="ghost"
          disabled
          className={cn(
            'h-12 w-full justify-start gap-2 px-2',
            'group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0',
            className,
          )}
        >
          <AvatarGlyph />
        </Button>
      );
    }
    return (
      <Button variant="ghost" size="icon" disabled className={className}>
        <AvatarGlyph />
      </Button>
    );
  }

  if (principal.status !== 'authenticated') {
    return (
      <Button
        size="sm"
        onClick={() => {
          void signIn();
        }}
        className={className}
      >
        <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
        Sign in
      </Button>
    );
  }

  const { user } = principal;
  const displayName = user.displayName ?? 'Signed in';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'expanded' ? (
          <Button
            variant="ghost"
            aria-label="Account menu"
            className={cn(
              'h-12 w-full justify-start gap-2 px-2',
              'group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0',
              className,
            )}
          >
            <AvatarGlyph />
            <span className="flex min-w-0 flex-1 flex-col items-start text-left leading-tight group-data-[collapsible=icon]:hidden">
              <span className="w-full truncate text-sm font-medium">{displayName}</span>
              {user.email !== undefined ? (
                <span className="w-full truncate text-xs text-muted-foreground">{user.email}</span>
              ) : null}
            </span>
            <EllipsisVertical
              className="ml-auto h-4 w-4 shrink-0 group-data-[collapsible=icon]:hidden"
              aria-hidden="true"
            />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Account menu"
            className={cn('h-10 w-10 rounded-full', className)}
          >
            <AvatarGlyph />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="font-medium">{displayName}</span>
          {user.email !== undefined ? (
            <span className="text-xs text-muted-foreground">{user.email}</span>
          ) : null}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/me">
            <User className="mr-2 h-4 w-4" aria-hidden="true" />
            Profile
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <ThemeMenuItems />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void signOut();
          }}
        >
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
